import React from 'react';
import { Button, Card, Col, Row, Spin, Table, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import customerLifecycleService from '@/services/customerLifecycle.service';

const { Title } = Typography;

const CustomerSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['customer-success-dashboard'],
    queryFn: () => customerLifecycleService.getSuccessDashboard(),
  });

  const { data: surveys = [] } = useQuery({
    queryKey: ['customer-success-surveys'],
    queryFn: async () => {
      const [pending, sent] = await Promise.all([
        customerLifecycleService.listSurveys({ status: 'Pending' }),
        customerLifecycleService.listSurveys({ status: 'Sent' }),
      ]);
      const byId = new Map<string, Record<string, unknown>>();
      for (const row of [...pending, ...sent] as Record<string, unknown>[]) {
        if (row?.id) byId.set(String(row.id), row);
      }
      return Array.from(byId.values()).sort((a, b) =>
        String(b.created_at || '').localeCompare(String(a.created_at || ''))
      );
    },
  });

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Customer Success
          </Title>
        </Col>
        <Col>
          <Button type="primary" onClick={() => navigate('/growth/customer-satisfaction')}>
            Customer Satisfaction Register
          </Button>
        </Col>
      </Row>
      <Spin spinning={isLoading}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>Awaiting responses: {(dashboard as any)?.pending_surveys ?? 0}</Card>
          </Col>
          <Col span={6}>
            <Card>Completed: {(dashboard as any)?.completed_surveys ?? 0}</Card>
          </Col>
          <Col span={6}>
            <Card>Avg satisfaction %: {(dashboard as any)?.avg_satisfaction_pct ?? '—'}</Card>
          </Col>
          <Col span={6}>
            <Card>Unsatisfied: {(dashboard as any)?.unsatisfied_surveys ?? 0}</Card>
          </Col>
        </Row>
        <Card title="Pending satisfaction surveys">
          <Table
            rowKey="id"
            size="small"
            dataSource={surveys as Record<string, unknown>[]}
            onRow={(r) => ({
              onClick: () => navigate(`/growth/customer-satisfaction/${r.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: 'Survey',
                dataIndex: 'survey_number',
                render: (v: string | null) => v || '—',
              },
              {
                title: 'Customer',
                dataIndex: 'customer_name',
                render: (_: unknown, r: Record<string, unknown>) =>
                  (r.customer_name as string) ||
                  (r.customer_name_snapshot as string) ||
                  '—',
              },
              { title: 'Type', dataIndex: 'survey_type' },
              { title: 'Status', dataIndex: 'status' },
            ]}
          />
        </Card>
      </Spin>
    </div>
  );
};

export default CustomerSuccessPage;
